import React, { createContext, useRef, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { purchase as purchaseApi } from 'js-api-client';
import { getSessionId } from 'utils/session';
import { setCybersourceKeys } from '@/actions/config';
import usePurchase from 'hooks/store/usePurchase';
import { trackEvent } from 'user-analytics';
import { DIGITAL_CARD_RECOMMENDATION_SHOWED } from 'constants/TrackEvents';
import { CardBrands } from '../../utils/cards';
import { getBinFromCybersourceToken } from '../../utils/Cybersource';

export const CybersourceMicroformFormContext = createContext();

/**
 * Disable Payment Request API to prevent Google Pay drawer in mobile
 */
function disablePaymentRequestAPI() {
  try {
    // Disable if we are in a mobile environment
    if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      Object.defineProperty(window, 'PaymentRequest', {
        configurable: true,
        get: () => undefined,
      });
      console.log('Payment Request API disabled to prevent Google Pay drawer');
    }
  } catch (err) {
    console.error('Failed to disable Payment Request API:', err);
  }
}

/**
 * Maps card brands to CyberSource type codes according to official documentation
 * @param {string} cardBrand - The card brand
 * @returns {string} The CyberSource type code
 */
const getCardTypeCode = (cardBrand) => {
  const cardTypeCodes = {
    [CardBrands.VISA]: '001', // Visa (includes Visa Electron)
    [CardBrands.ELECTRON]: '001', // Visa Electron - use 001 as per documentation
    [CardBrands.MASTERCARD]: '002', // Mastercard, Eurocard
    [CardBrands.AMEX]: '003', // American Express
    [CardBrands.DISCOVER]: '004', // Discover
    [CardBrands.DINERS]: '005', // Diners Club
    [CardBrands.JCB]: '007', // JCB
    [CardBrands.MAESTRO]: '042', // Maestro International
    [CardBrands.UNION_PAY]: '062', // China UnionPay
  };

  return cardTypeCodes[cardBrand]; // Return undefined if unknown, let CyberSource handle it
};

/**
 * Maps the CyberSource response to the expected format.
 * @param {string} token - The token
 * @param {Object} cardParams - The card parameters
 * @returns {Object} The mapped CyberSource response
 */
const mappedCyberResponse = (token, cardParams) => {
  return {
    credit_card_tokens: {
      cybersource_transient_token: token,
    },
    card_holder_name: cardParams.holderName,
    card_brand: cardParams.cardBrand,
  };
};

/**
 * Parse expiration date string in format MM/YY or MM/YYYY
 * @param {string} expirationDate - Expiration date in format MM/YY or MM/YYYY
 * @returns {Array} Array with [month, year]
 */
const parseExpirationDate = (expirationDate) => {
  const [month, year] = expirationDate.split('/');
  const fullYear = year.length === 2 ? `20${year}` : year;
  return [month, fullYear];
};

/**
 * CybersourceMicroformFormProvider component.
 * Provides context for the CybersourceMicroformForm component.
 *
 * @param {object} props - children.
 * @param {React.ReactNode} props.children - Child components to render.
 * @returns {React.ReactElement} Provider component.
 */
export const CybersourceMicroformFormProvider = ({ children }) => {
  // Deshabilitar la Payment Request API al cargar el componente
  useEffect(() => {
    disablePaymentRequestAPI();
  }, []);
  const [initialized, setInitialized] = useState({
    flex: false,
    microform: false,
    antifraudBeacon: false,
  });
  const [formValues, setFormValues] = useState({
    cardNumberValid: false,
    cardSecurityCodeValid: false,
    cardBrand: '',
    cybsCardType: '',
    holderName: '',
    expirationMonth: '',
    expirationYear: '',
    billingAddress: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [binMessageVisible, setBinMessageVisible] = useState(false);

  const dispatch = useDispatch();
  const { features, env } = useSelector((state) => state.whitelabelConfig);
  const { paymentMethods } = usePurchase();
  const { cybersourceLocalKeys } = useSelector((state) => state.config);
  const purchase = useSelector((state) => state.purchase);
  const purchaseToken = purchase?.get('token');
  const paymentCredentials = purchase?.get('paymentCredentials')?.toJS();
  const cybersourceKeys = paymentCredentials?.creditCard?.cybersource;

  const flex = useRef(null);
  const microform = useRef(null);
  const fields = useRef({});
  const antifraudBeaconLoaded = useRef(false);
  const isMountedRef = useRef(true);
  const binCheckedRef = useRef(false);
  // Bumped on every BIN check (and whenever inputs change); only the latest check may update state.
  const binCheckRequestIdRef = useRef(0);

  // Prevent state updates after the component unmounts
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Find JWT token for Cybersource payment method
   * @returns {string} The JWT token for Cybersource payment method
   */
  const findJWT = useCallback(() => {
    const cybersourceMethod = paymentMethods?.find((method) => method.engine === 'cybersource');
    return cybersourceMethod?.extra?.paymentGatewayToken?.jwt;
  }, [paymentMethods]);

  /**
   * Initialize Cybersource Flex SDK
   */
  const initializeFlex = useCallback(() => {
    if (initialized.flex) return flex.current;

    const captureContext = findJWT();
    if (!captureContext) {
      console.error('No Cybersource JWT token found');
      return null;
    }

    try {
      // The script should already be loaded from initializers.js
      // eslint-disable-next-line no-undef
      flex.current = new Flex(captureContext);
      setInitialized((prev) => ({ ...prev, flex: true }));
      return flex.current;
    } catch (err) {
      console.error('Failed to initialize Cybersource Flex:', err);
      return null;
    }
  }, [initialized.flex, findJWT]);

  /**
   * Initialize Cybersource Microform
   */
  const initializeMicroform = useCallback(() => {
    if (!flex.current || initialized.microform) return;

    try {
      // These styles are necessary because we need to send them to the cybersource render
      const styles = {
        input: {
          'font-size': '16px',
        },
        valid: { color: '#3c763d' },
        invalid: { color: '#ff040d' },
      };

      microform.current = flex.current.microform({
        styles,
        autocomplete: 'off',
        autocorrect: 'off',
        spellcheck: 'false',
      });
      setInitialized((prev) => ({ ...prev, microform: true }));
      return microform.current;
    } catch (err) {
      console.error('Failed to initialize Cybersource Microform:', err);
      return null;
    }
  }, [initialized.microform]);

  /**
   * Set form field value
   * @param {string} field - Field name
   * @param {any} value - Field value
   */
  const setFieldValue = useCallback((field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  /**
   * Create a Microform field
   * @param {object} params - Field parameters
   * @param {string} params.fieldType - Field type (number, securityCode)
   * @param {object} params.options - Field options
   * @param {string} params.containerId - Container ID to load the field into
   * @returns {object|null} The created field or null if creation failed
   */
  const createField = useCallback(
    ({ fieldType, options, containerId }) => {
      if (!microform.current || !initialized.microform) return null;

      try {
        if (fields.current[fieldType]) {
          if (containerId && !fields.current[fieldType].loaded) {
            fields.current[fieldType].load(containerId);
          }
          return fields.current[fieldType];
        }

        const field = microform.current.createField(fieldType, {
          styles: {
            input: {
              'font-size': '16px',
            },
            valid: { color: '#3c763d' },
            invalid: { color: '#ff040d' },
          },
          autocomplete: 'off',
          autocorrect: 'off',
          ...options,
        });

        field.on('change', (data) => {
          const fieldHandlers = {
            number: () => {
              const brand = data.card?.[0]?.name || '';
              const cybsCardType = data.card?.[0]?.cybsCardType || '';

              setFieldValue('cardNumberValid', data.valid);
              if (brand) setFieldValue('cardBrand', brand);
              if (cybsCardType) setFieldValue('cybsCardType', cybsCardType);
            },
            securityCode: () => {
              setFieldValue('cardSecurityCodeValid', data.valid);
            },
          };
          fieldHandlers[fieldType]?.();
        });

        fields.current[fieldType] = field;
        if (containerId) {
          field.load(containerId);
        }

        return field;
      } catch (err) {
        console.error(`Failed to create ${fieldType} field:`, err);
        return null;
      }
    },
    [initialized.microform, setFieldValue],
  );

  /**
   * Check if Cybersource SDK script is loaded
   */
  const isCybersourceScriptLoaded = useCallback(() => {
    return typeof Flex !== 'undefined';
  }, []);

  /**
   * Load the antifraud beacon for Cybersource
   * @param {Object} options - Options for loading the beacon
   * @param {string} options.orgId - Organization ID
   * @param {string} options.merchantId - Merchant ID
   * @param {string} options.sessionId - Session ID
   * @returns {boolean} - Whether the beacon was loaded successfully
   */
  const loadAntifraudBeacon = useCallback(
    (options) => {
      if (antifraudBeaconLoaded.current) return true;

      try {
        const { orgId, sessionId, merchantId } = options;
        const url = `//h.online-metrix.net/fp/tags.js?org_id=${orgId}&session_id=${merchantId}${sessionId}`;

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = false;
        script.defer = true;
        script.src = url;

        const existingScript = document.getElementsByTagName('script')[0];
        existingScript.parentNode.insertBefore(script, existingScript);

        antifraudBeaconLoaded.current = true;
        setInitialized((prev) => ({ ...prev, antifraudBeacon: true }));
        dispatch(setCybersourceKeys(options));
        return true;
      } catch (err) {
        console.error('Failed to load Cybersource antifraud beacon:', err);
        return false;
      }
    },
    [dispatch],
  );

  /**
   * Initialize Cybersource SDK and Microform
   */
  const initializeCybersource = useCallback(async () => {
    setIsLoading(true);

    try {
      // Check if Cybersource script is already loaded (from initializers.js)
      if (!isCybersourceScriptLoaded()) {
        console.error("Cybersource SDK not loaded. Make sure it's loaded in initializers.js");
        return;
      }

      const flexInstance = initializeFlex();
      if (flexInstance) {
        initializeMicroform();
      }
    } catch (err) {
      console.error('Error initializing Cybersource:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isCybersourceScriptLoaded, initializeFlex, initializeMicroform]);

  /**
   * Create payment payload with card data
   * @param {object} cardData - Card data
   * @returns {Promise<object>} Payment payload
   */
  const createPaymentPayload = useCallback(async (cardData) => {
    if (!microform.current) {
      throw new Error('Cybersource Microform not initialized');
    }

    setIsLoading(true);
    try {
      const { holderName, cardExpiration, cardBrand, cybsCardType } = cardData;

      let expirationMonth;
      let expirationYear;

      if (cardExpiration) {
        [expirationMonth, expirationYear] = parseExpirationDate(cardExpiration);
      } else {
        expirationMonth = cardData.expirationMonth;
        expirationYear = cardData.expirationYear;
      }

      const cardParams = {
        holderName,
        cardBrand: cardBrand || 'visa',
        expirationMonth,
        expirationYear,
        cybsCardType,
      };

      // Create options for token creation
      const options = {
        expirationMonth,
        expirationYear,
      };

      // Add card type code if available
      const cardTypeCode = getCardTypeCode(cardBrand);
      if (cardTypeCode) {
        options.type = cardTypeCode;
      }

      // Create token using microform
      return new Promise((resolve, reject) => {
        microform.current.createToken(options, (error, token) => {
          if (error) {
            reject(error);
            return;
          }
          resolve(mappedCyberResponse(token, cardParams));
        });
      });
    } catch (error) {
      console.error('Error creating payment payload:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Reset form values and errors
   */
  const resetForm = useCallback(() => {
    setFormValues({
      cardNumberValid: false,
      cardSecurityCodeValid: false,
      cardBrand: '',
      cybsCardType: '',
      holderName: '',
      expirationMonth: '',
      expirationYear: '',
      billingAddress: '',
    });
  }, []);

  /**
   * Check if form is valid
   * @returns {boolean} True if form is valid
   */
  const isFormValid = useCallback(() => {
    return (
      formValues.cardNumberValid &&
      formValues.cardSecurityCodeValid &&
      !!formValues.holderName &&
      !!formValues.expirationMonth &&
      !!formValues.expirationYear
    );
  }, [formValues]);

  /**
   * Check whether the entered card BIN is in the tenant's list and, if so, show the
   * digital-card recommendation message.
   *
   * Cybersource hosted fields never expose raw card digits in field events, so the BIN is
   * obtained from the transient token created here and decoded from its JWT payload.
   */
  const checkDigitalCardRecommendation = useCallback(async () => {
    if (!microform.current) return;

    binCheckRequestIdRef.current += 1;
    const requestId = binCheckRequestIdRef.current;
    // True only while this is still the most recent check and the component is mounted.
    // eslint-disable-next-line jsdoc/require-jsdoc
    const isLatest = () => isMountedRef.current && requestId === binCheckRequestIdRef.current;

    // createToken needs a real expiration (plus valid number + security-code hosted fields). This
    // token is only used to read the BIN and is discarded.
    const options = {
      expirationMonth: formValues.expirationMonth,
      expirationYear: formValues.expirationYear,
    };
    const cardTypeCode = getCardTypeCode(formValues.cardBrand);
    if (cardTypeCode) options.type = cardTypeCode;

    try {
      const token = await new Promise((resolve, reject) => {
        microform.current.createToken(options, (error, createdToken) => {
          if (error) {
            reject(error);
            return;
          }
          resolve(createdToken);
        });
      });

      const bin = getBinFromCybersourceToken(token);
      if (!bin) {
        if (isLatest()) setBinMessageVisible(false);
        return;
      }

      const response = await purchaseApi.validateInstallments({ bin }, { engine: 'cybersource' });
      if (!isLatest()) return;

      setBinMessageVisible(response.eligible ?? false);
      if (response.eligible) {
        // Don't forward the card BIN to analytics vendors; the event only needs
        // to measure that the recommendation was shown.
        trackEvent(DIGITAL_CARD_RECOMMENDATION_SHOWED, { Engine: 'cybersource' });
      }
    } catch (error) {
      console.error('Error checking Cybersource digital-card recommendation:', error);
      if (isLatest()) setBinMessageVisible(false);
    }
  }, [formValues.expirationMonth, formValues.expirationYear, formValues.cardBrand]);

  // Check the BIN once the card number, security code and expiration are valid — createToken
  // tokenizes the hosted number + security-code fields and needs a real expiration. The holder
  // name is NOT required. The BIN comes from a transient token (hosted fields never expose raw
  // digits). Gated by the flag.
  useEffect(() => {
    if (!features.SHOW_DIGITAL_CARD_RECOMMENDATION) return;

    const readyToCheck =
      formValues.cardNumberValid &&
      formValues.cardSecurityCodeValid &&
      !!formValues.expirationMonth &&
      !!formValues.expirationYear;

    if (readyToCheck && !binCheckedRef.current) {
      binCheckedRef.current = true;
      checkDigitalCardRecommendation();
    } else if (!formValues.cardNumberValid || !formValues.cardSecurityCodeValid) {
      // The number or CVV is being edited; reset so the BIN is re-checked once both are valid
      // again, and hide the message meanwhile. Bump the request id so any in-flight check is ignored.
      binCheckRequestIdRef.current += 1;
      binCheckedRef.current = false;
      setBinMessageVisible(false);
    }
  }, [
    features.SHOW_DIGITAL_CARD_RECOMMENDATION,
    formValues.cardNumberValid,
    formValues.cardSecurityCodeValid,
    formValues.expirationMonth,
    formValues.expirationYear,
    checkDigitalCardRecommendation,
  ]);

  /**
   * Initialize antifraud beacon
   */
  const initAntifraudBeacon = useCallback(() => {
    // Check if we need to use dynamic credentials
    const useDynamicCredentials = features.DYNAMIC_CYBERSOURCE_CREDENTIALS;

    if (!env?.cybersource?.enabled) return;

    // Get session ID for antifraud
    const sessionId = getSessionId();

    // Default to environment config
    let beaconOptions = {
      orgId: env.cybersource.orgId,
      merchantId: env.cybersource.merchantId,
      enabled: env.cybersource.enabled,
      sessionId,
    };

    // If using dynamic credentials and we have a purchase token
    if (useDynamicCredentials && purchaseToken && cybersourceKeys) {
      const { merchantId, orgId } = cybersourceKeys;
      const { merchantId: merchantIdLocal, orgId: orgIdLocal } = cybersourceLocalKeys || {};

      // If the keys have changed, reload the page to reinitialize
      if (
        (merchantIdLocal && merchantIdLocal !== merchantId) ||
        (orgIdLocal && orgIdLocal !== orgId)
      ) {
        window.location.reload();
        return;
      }

      // If the keys are the same, no need to reinitialize
      if (merchantIdLocal && merchantIdLocal === merchantId && orgIdLocal && orgIdLocal === orgId) {
        return;
      }

      // Use the dynamic keys if available
      if (merchantId && orgId) {
        beaconOptions = {
          merchantId,
          orgId,
          enabled: env.cybersource.enabled,
          sessionId,
        };
      }
    }

    // Load the antifraud beacon
    loadAntifraudBeacon(beaconOptions);
  }, [
    env,
    features.DYNAMIC_CYBERSOURCE_CREDENTIALS,
    purchaseToken,
    cybersourceKeys,
    cybersourceLocalKeys,
    loadAntifraudBeacon,
  ]);

  // Load antifraud beacon when cybersource keys are available
  useEffect(() => {
    initAntifraudBeacon();
  }, [initAntifraudBeacon]);

  // Expose context values
  const contextValue = {
    formValues,
    setFieldValue,
    createField,
    createPaymentPayload,
    initializeCybersource,
    resetForm,
    isFormValid,
    isLoading,
    initialized,
    binMessageVisible,
    features: features || {},
  };

  return (
    <CybersourceMicroformFormContext.Provider value={contextValue}>
      {children}
    </CybersourceMicroformFormContext.Provider>
  );
};

CybersourceMicroformFormProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
